import { lazy, Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { trackNavigation } from './utils/sentry';
import { useWellnessStore } from '@/stores/wellness-store';
import { identifyWellnessUser } from '@/utils/authUtils';
import { isNativePlatform } from '@/utils/capacitor';

// Feature-level lazy chunks — each import() is a hard code-split boundary.
// Rolldown will create a separate JS file for each feature.
const LandingPage = lazy(() => import('@/features/marketing/pages/LandingPage'));
const SciencePage = lazy(() => import('@/features/marketing/pages/SciencePage'));
const ResourcesPage = lazy(() => import('@/features/marketing/pages/ResourcesPage'));
const AboutPage = lazy(() => import('@/features/marketing/pages/AboutPage'));
const LearnRoutes = lazy(() => import('@/features/marketing/pages/LearnRoutes'));
const PersonalizedResultsPage = lazy(
  () => import('@/features/marketing/pages/PersonalizedResultsPage')
);
const BrandingSamplePage = lazy(
  () => import('@/features/marketing/pages/BrandingSamplePage')
);
const SurveyRoutes = lazy(() => import('@/features/participant-survey/SurveyRoutes'));
const WellnessRoutes = lazy(() => import('@/features/wellness/WellnessRoutes'));
const PreviewRoutes = lazy(() => import('@/features/preview/PreviewRoutes'));
const ParticipantSelfRegistrationPage = lazy(
  () => import('@/features/participant-survey/pages/ParticipantSelfRegistrationPage')
);

function App() {
  const location = useLocation();
  const previousPathname = useRef<string | null>(null);
  const tokens = useWellnessStore((state) => state.tokens);
  const user = useWellnessStore((state) => state.user);
  const subscriptionStatus = useWellnessStore((state) => state.subscriptionStatus);
  const fetchSubscriptionStatus = useWellnessStore((state) => state.fetchSubscriptionStatus);

  // Identify user in Sentry and Mixpanel on app load if already authenticated
  useEffect(() => {
    if (tokens?.idToken && user) {
      identifyWellnessUser(tokens.idToken, {
        email: user.email,
        b2c: user.b2c,
        needsProfile: user.needsProfile,
      });
    }
  }, []); // Only run once on mount

  // Ensure subscription status is loaded when app starts with an authenticated user
  // or when tokens become available (e.g., opening a new tab).
  useEffect(() => {
    if (!tokens?.idToken) return;
    if (subscriptionStatus !== 'idle') return;

    fetchSubscriptionStatus().catch((err) => {
      // Don't block app load if subscription status fails to load
      // Errors are already handled by wellness-store / axios interceptors.
      console.error('[App] Failed to fetch subscription status on app load:', err);
    });
  }, [tokens?.idToken, subscriptionStatus, fetchSubscriptionStatus]);

  // Scroll to top on every route change so the new page starts at the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Track route changes (including initial page load)
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathname.current;

    if (previousPath === null || previousPath !== currentPath) {
      trackNavigation(previousPath || '', currentPath);
      previousPathname.current = currentPath;
    }
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Suspense fallback={null}>
        <Routes location={location} key={location.pathname}>
          {/* Marketing — smallest chunk, loaded first */}
          <Route path="/" element={isNativePlatform ? <Navigate to="/wellness/login" replace /> : <LandingPage />} />
          <Route path="/science" element={isNativePlatform ? <Navigate to="/wellness/login" replace /> : <SciencePage />} />
          <Route path="/resources" element={isNativePlatform ? <Navigate to="/wellness/login" replace /> : <ResourcesPage />} />
          <Route path="/about" element={isNativePlatform ? <Navigate to="/wellness/login" replace /> : <AboutPage />} />
          <Route
            path="/learn/*"
            element={isNativePlatform ? <Navigate to="/wellness/login" replace /> : <LearnRoutes />}
          />
          <Route
            path="/personalized-results"
            element={
              isNativePlatform ? (
                <Navigate to="/wellness/login" replace />
              ) : (
                <PersonalizedResultsPage />
              )
            }
          />
          <Route
            path="/branding-sample"
            element={
              isNativePlatform ? (
                <Navigate to="/wellness/login" replace />
              ) : (
                <BrandingSamplePage />
              )
            }
          />

          {/* Survey feature — lazy chunk (includes survey pages + all translations) */}
          <Route path="/survey/*" element={<SurveyRoutes />} />

          {/* Preview Assessment — public unauthenticated preview flow */}
          <Route path="/preview/*" element={<PreviewRoutes />} />

          {/* Self-registration — lazy, separate from main survey flow */}
          <Route path="/self/:surveyId/add" element={<ParticipantSelfRegistrationPage />} />

          {/* Wellness feature — lazy chunk (includes all wellness pages + guards) */}
          <Route path="/wellness/*" element={<WellnessRoutes />} />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/wellness" replace />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}

export default App;
